import { useCallback, useRef } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { BranchStep } from 'dorian-shared/types/branch/BranchStep';
import {
  HubProgressBar,
  HubSettings,
} from 'dorian-shared/types/branch/HubOption';
import { createDefaultProgressBar, HUB_MAX_PROGRESS_BARS } from '../hubStepDefaults';

type UseHubProgressBarsParams = {
  step: BranchStep;
  stepIndex: number;
  updateStep: (index: number, step: BranchStep) => void;
};

export function useHubProgressBars({ step, stepIndex, updateStep }: UseHubProgressBarsParams) {
  const stepRef = useRef(step);
  stepRef.current = step;

  const bars: HubProgressBar[] = step.hubSettings?.progressBars ?? [];

  const updateBars = useCallback(
    (nextBars: HubProgressBar[]) => {
      const reindexed = nextBars.map((bar, i) => ({ ...bar, sortOrder: i }));
      const currentSettings: HubSettings = stepRef.current.hubSettings ?? {
        header: { text: '' },
        subheader: null,
      };
      updateStep(stepIndex, {
        ...stepRef.current,
        hubSettings: { ...currentSettings, progressBars: reindexed },
      });
    },
    [stepIndex, updateStep],
  );

  const addBar = useCallback(() => {
    const current = stepRef.current.hubSettings?.progressBars ?? [];
    if (current.length >= HUB_MAX_PROGRESS_BARS) return;
    updateBars([...current, createDefaultProgressBar(current.length)]);
  }, [updateBars]);

  const updateBar = useCallback(
    (uuid: string, changes: Partial<HubProgressBar>) => {
      const current = stepRef.current.hubSettings?.progressBars ?? [];
      updateBars(current.map((bar) => (bar.uuid === uuid ? { ...bar, ...changes } : bar)));
    },
    [updateBars],
  );

  const removeBar = useCallback(
    (uuid: string) => {
      const current = stepRef.current.hubSettings?.progressBars ?? [];
      updateBars(current.filter((bar) => bar.uuid !== uuid));
    },
    [updateBars],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const current = stepRef.current.hubSettings?.progressBars ?? [];
      const oldIndex = current.findIndex((b) => b.uuid === active.id);
      const newIndex = current.findIndex((b) => b.uuid === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      updateBars(arrayMove(current, oldIndex, newIndex));
    },
    [updateBars],
  );

  const isAtLimit = bars.length >= HUB_MAX_PROGRESS_BARS;

  return {
    bars,
    isAtLimit,
    addBar,
    updateBar,
    removeBar,
    handleDragEnd,
  };
}
